import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

import SubscriptionStore from '../../redux/subscriptions';
import type { FossaPlanName } from '../../../shared/subscriptions';

type UpdateSubscriptionParams = {
  plan: FossaPlanName;
  contributors?: number;
};

/**
 * This hook is the starting point for a central hook for subcripiton based actions.
 * Right now, it only provides a function for updating subscription, but it can (and should) be expanded with
 * more subscription operations.
 *
 * @deprecated This is the old way of doing things with Stripe. Please use useGetCurrentPlan, useUpdatePlan and others.
 */
const useSubscription = () => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  if (!stripe || !elements) {
    throw new Error('Could not find stripe!');
  }

  /**
   * Hook for updating Stripe subscriptions. Calls `SubscriptionManager.updatePlan`.
   * This hook will look for a CreditCard element in the component it's used. If you are using one, make sure not to
   * hide the credit card element until **after** this function is called.
   * @param {UpdateSubscriptionParams} params - The parameters that will be passed to the SubscriptionManager.
   * @param {FossaPlanName} params.plan - The name of the plan to change to. Use the existing plan name if this value
   *                                      isn't changing.
   * @param {number} params.contributors - (Optional) The number of contributors to set the subscription to.
   */
  const updateSubscription = useCallback(async (
    params: UpdateSubscriptionParams,
  ) => {
    const cardElement = elements.getElement(CardElement);

    // The cardToken is optional in cases where we're only updating the plan or contributors.
    let cardToken = {};
    if (cardElement) {
      const stripeResult = await stripe.createToken(cardElement);

      if (stripeResult.token) {
        cardToken = {
          token: stripeResult.token.id,
        };
      }
    }

    dispatch(
      SubscriptionStore.actions.updateSubscription({
        ...cardToken,
        ...params,
      }),
    );
  }, [dispatch, elements, stripe]);

  return {
    updateSubscription,
  };
};

export default useSubscription;
